import { TaggedRow, TagInfo, TagType, EntityUpdate } from "./types";

// Constants
export const MAX_RECENT_TAGS = 5;

// Calculate the tags that should be on each entity
export const calculateEntityUpdates = <T extends TaggedRow>(
  selectedTags: TagInfo[],
  selectedRows: T[],
  getEntityId: (row: T) => string
): EntityUpdate[] => {
  const entityUpdates: EntityUpdate[] = [];

  // Get tags that should be on ALL entities (NEW and ALL types)
  const universalTags = selectedTags
    .filter((tag) => tag.type === TagType.NEW || tag.type === TagType.ALL)
    .map((t) => t.name);

  // Calculate final tags for each entity
  selectedRows.forEach((row) => {
    const finalTagsSet = new Set<string>();

    // Add universal tags (NEW and ALL)
    universalTags.forEach((tag) => finalTagsSet.add(tag));

    // Add partial tags only if this entity currently has them
    selectedTags
      .filter((tag) => tag.type === TagType.PARTIAL)
      .forEach((partialTag) => {
        if (row.tags) {
          const hasTag =
            Array.isArray(row.tags) &&
            row.tags.some((tag) => {
              if (typeof tag === "string") {
                return tag === partialTag.name;
              } else {
                return tag.name === partialTag.name;
              }
            });
          if (hasTag) {
            finalTagsSet.add(partialTag.name);
          }
        }
      });

    const finalTags = Array.from(finalTagsSet).sort();

    // Extract current tag names, handling both string and object formats
    const currentTags = (row.tags || [])
      .map((tag) => {
        if (typeof tag === "string") {
          return tag;
        } else {
          return tag.name;
        }
      })
      .sort();

    // Only include entities that have changes
    if (currentTags.join(",") !== finalTags.join(",")) {
      const entityId = getEntityId(row);
      const update: EntityUpdate = {
        tags: finalTags,
        id: entityId,
      };
      entityUpdates.push(update);
    }
  });

  return entityUpdates;
};

// Internal tag analysis function that handles both string and object tag formats
export const calculateTagAnalysis = <T extends TaggedRow>(
  selectedRows: T[]
) => {
  if (!selectedRows.length) return { allTags: [], partialTags: [] };

  const tagCounts = new Map<string, number>();
  const totalEntities = selectedRows.length;

  selectedRows.forEach((row) => {
    if (row.tags) {
      row.tags.forEach((tag) => {
        const tagName = typeof tag === "string" ? tag : tag.name;
        tagCounts.set(tagName, (tagCounts.get(tagName) || 0) + 1);
      });
    }
  });

  const allTags: string[] = [];
  const partialTags: string[] = [];

  tagCounts.forEach((count, tag) => {
    if (count === totalEntities) {
      allTags.push(tag);
    } else {
      partialTags.push(tag);
    }
  });

  return { allTags: allTags.sort(), partialTags: partialTags.sort() };
};

// Sort tags by type first: NEW, then ALL, then PARTIAL, then alphabetically
export const tagSortComparator = (a: TagInfo, b: TagInfo): number => {
  // Sort by type first: NEW, then ALL, then PARTIAL
  const typeOrder = {
    [TagType.NEW]: 0,
    [TagType.ALL]: 1,
    [TagType.PARTIAL]: 2,
  };
  const aOrder = typeOrder[a.type];
  const bOrder = typeOrder[b.type];

  if (aOrder !== bOrder) {
    return aOrder - bOrder;
  }

  // Then by alphabetical
  return a.name.localeCompare(b.name);
};

// Check if there are changes to the tags
export const calculateHasChanges = (
  selectedTags: TagInfo[],
  initialSnapshot: TagInfo[]
): boolean => {
  // If the number of tags changed, there are changes
  if (selectedTags.length !== initialSnapshot.length) {
    return true;
  }

  // Create sorted arrays of tag names for comparison
  const currentTagNames = selectedTags.map((t) => t.name).sort();
  const initialTagNames = initialSnapshot.map((t) => t.name).sort();

  // Check if tag names are different
  if (currentTagNames.join(",") !== initialTagNames.join(",")) {
    return true;
  }

  // Check if tag types have changed (e.g., partial promoted to all)
  const currentTagMap = new Map(selectedTags.map((t) => [t.name, t.type]));
  const initialTagMap = new Map(initialSnapshot.map((t) => [t.name, t.type]));

  for (const [tagName, currentType] of currentTagMap) {
    const initialType = initialTagMap.get(tagName);
    if (currentType !== initialType) {
      return true;
    }
  }

  return false;
};

export const createInitialTags = (tagAnalysis: {
  allTags: string[];
  partialTags: string[];
}): TagInfo[] => {
  return [
    ...tagAnalysis.allTags.map((tag: string) => ({
      name: tag,
      type: TagType.ALL,
      originallyPartial: false,
    })),
    ...tagAnalysis.partialTags.map((tag: string) => ({
      name: tag,
      type: TagType.PARTIAL,
      originallyPartial: true,
    })),
  ].sort((a, b) => a.name.localeCompare(b.name));
};
